import { matchPath, useLocation } from 'react-router-dom'
import { useSafeNavigate } from '@/shared/lib/safe-navigate'
import { Wrapper } from '../shortcut-bar/style'
import { SidebarMenu } from '@/shared/ui/organisms/sidebar-menu'
import { getCookie } from '@/shared/lib/formatter'
import { storage } from '@/shared/storage'
import { useTranslation } from 'react-i18next'

import { EyeIcon, EyeSlashIcon, MenuIcon } from '@/shared/ui/icons'
import { useState, useMemo } from 'react'
import {
  useGroupedMenuItems,
  useGroupedMenuWarehouseId,
  useGroupedMenuItemsDataLibrary,
  useGroupedMenuSupply,
  useGroupedMenuIncomingDocuments,
  useGroupedMenuFinance,
  useProfileMenuItems,
  useGroupedMenuSales,
  useGroupedMenuHR,
  useGroupedMenuProduction,
  useGroupedMenuGate,
  useGroupedMenuAccounting,
} from '../../pages/Warehouse/MainComponents/MenuItems'
import { ConfigProvider } from 'antd'
import { useThemeObject } from '@/entities/theme/model'

interface SidebarProps {
  collapsed: boolean
  handleToggle: () => void
}

const Sidebar = ({ collapsed, handleToggle }: SidebarProps) => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useSafeNavigate()
  const { pathname } = location
  const [activePath, setActivePath] = useState('')
  const theme = useThemeObject()
  // URL is the source of truth; cookie is the cross-route fallback; namespaced storage is the final fallback
  const warehouseMatch = matchPath('/warehouse/stocks-management/:id/*', pathname)
  const warehouseId =
    warehouseMatch?.params?.id ?? getCookie('warehouse_id') ?? storage.ui.getWarehouseId() ?? ''

  const groupedMenuItems = useGroupedMenuItems()
  const profileMenuItems = useProfileMenuItems()
  const groupedMenuItemsDataLibrary = useGroupedMenuItemsDataLibrary()
  const groupedMenuWarehouseId = useGroupedMenuWarehouseId(warehouseId)
  const groupedMenuSupply = useGroupedMenuSupply()
  const groupedMenuIncomingDocuments = useGroupedMenuIncomingDocuments()
  const groupedMenuFinance = useGroupedMenuFinance()
  const groupedMenuSales = useGroupedMenuSales()
  const groupedMenuHR = useGroupedMenuHR()
  const groupedMenuProduction = useGroupedMenuProduction()
  const groupedMenuGate = useGroupedMenuGate()
  const groupedMenuAccounting = useGroupedMenuAccounting()

  // Memoize menuTheme to prevent ConfigProvider re-renders
  const menuTheme = useMemo(
    () => ({
      components: {
        Menu: {
          itemHoverBg: theme.sidebarItemHover,
          itemActiveBg: theme.sidebarItemActive,
          itemActiveBlur: theme.sidebarItemActiveBlur,
          itemHoverColor: theme.sidebarTextColor,
        },
      },
    }),
    [
      theme.sidebarItemHover,
      theme.sidebarItemActive,
      theme.sidebarItemActiveBlur,
      theme.sidebarTextColor,
    ],
  )

  // Memoize which menu section is active based on pathname
  const activeMenuItems = useMemo(() => {
    if (pathname.startsWith('/data-library')) return groupedMenuItemsDataLibrary
    if (matchPath('/warehouse/stocks-management/:id/*', pathname)) return groupedMenuWarehouseId
    if (matchPath('/warehouse', pathname) || matchPath('/warehouse/stocks-management', pathname))
      return groupedMenuItems
    if (pathname.startsWith('/supply')) return groupedMenuSupply
    if (pathname.startsWith('/incoming-documents')) return groupedMenuIncomingDocuments
    if (pathname.startsWith('/finance')) return groupedMenuFinance
    if (pathname.startsWith('/user')) return profileMenuItems
    if (pathname.startsWith('/sales')) return groupedMenuSales
    if (pathname.startsWith('/hr')) return groupedMenuHR
    if (pathname.startsWith('/production')) return groupedMenuProduction
    if (pathname.startsWith('/gate')) return groupedMenuGate
    if (pathname.startsWith('/accounting')) return groupedMenuAccounting
    return null
  }, [
    pathname,
    groupedMenuItemsDataLibrary,
    groupedMenuWarehouseId,
    groupedMenuItems,
    groupedMenuSupply,
    groupedMenuIncomingDocuments,
    groupedMenuFinance,
    profileMenuItems,
    groupedMenuSales,
    groupedMenuHR,
    groupedMenuProduction,
    groupedMenuGate,
    groupedMenuAccounting,
  ])

  return (
    <Wrapper
      $sidebar={true}
      $collapsed={collapsed}
      theme={theme}
      $isnotauthorized={pathname === '/' || pathname === '/login' ? true : false}
    >
      <Wrapper.Top $isCollapsed={collapsed}>
        <MenuIcon
          width='18px'
          height='18px'
          color={theme.navigationTextColor}
          hovercolor={theme.navigationTextColor}
          onClick={() => navigate('/')}
        />

        {!collapsed && <div className='text-(--primary-btn-text)'>{t(`main`)}</div>}
        {!collapsed ? (
          <EyeSlashIcon
            width='24px'
            height='24px'
            color={theme.navigationTextColor}
            hovercolor={theme.navigationTextColor}
            onClick={handleToggle}
          />
        ) : (
          <EyeIcon
            width='24px'
            height='24px'
            color={theme.navigationTextColor}
            hovercolor={theme.navigationTextColor}
            onClick={handleToggle}
          />
        )}
      </Wrapper.Top>
      {activeMenuItems && (
        <ConfigProvider theme={menuTheme}>
          <SidebarMenu
            items={activeMenuItems as import('antd/es/menu/interface').ItemType[]}
            setActivePath={setActivePath}
            collapsed={collapsed}
          />
        </ConfigProvider>
      )}
    </Wrapper>
  )
}

export default Sidebar
